/**
 * Lightweight module-level pub/sub toast system.
 * No React context needed — just call showToast() anywhere.
 * Mount <ToastHost /> once in the layout to render the toasts.
 */

export type ToastType = 'error' | 'success' | 'info' | 'warning';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

type Subscriber = (toasts: Toast[]) => void;

let _toasts: Toast[] = [];
let _nextId = 0;
const _subscribers: Subscriber[] = [];

function _notify(): void {
  const snapshot = [..._toasts];
  _subscribers.forEach(fn => fn(snapshot));
}

/**
 * Show a toast notification. Auto-dismisses after ~6 s.
 */
export function showToast(message: string, type: ToastType = 'info'): void {
  const id = ++_nextId;
  _toasts = [..._toasts, { id, message, type }];
  _notify();
  setTimeout(() => {
    _toasts = _toasts.filter(t => t.id !== id);
    _notify();
  }, 6000);
}

/**
 * Programmatically dismiss a specific toast.
 */
export function dismissToast(id: number): void {
  _toasts = _toasts.filter(t => t.id !== id);
  _notify();
}

/**
 * Subscribe to toast list changes. Returns an unsubscribe function.
 */
export function subscribeToasts(fn: Subscriber): () => void {
  _subscribers.push(fn);
  fn([..._toasts]); // immediate snapshot on subscribe
  return () => {
    const idx = _subscribers.indexOf(fn);
    if (idx !== -1) _subscribers.splice(idx, 1);
  };
}
